import { create } from "zustand";
import { persist } from "zustand/middleware";
import LogUtil from "@/betby/utils/LogUtil";
import {
  createAppConfigPersistStorage,
  PERSIST_STORAGE_NAMES,
} from "@/betby/store/persistStorage";
import type {
  OAuthLoginUserinfo,
  OAuthLoginAccountInfo,
  OAuthLoginConnectionInfo,
  OAuthLoginBasic,
  PersistedLoginData,
} from "@/betby/api/types";

/**
 * 用户信息接口（兼容旧逻辑，来自 userinfo ?? account_info）
 */
interface UserInfo {
  currency_type?: string;
  [key: string]: unknown;
}

/**
 * 认证状态接口
 */
interface AuthState {
  access_token: string | null;
  refresh_token: string | null;
  expires_time: number | null;
  user_id: number | null;
  product_id: number | null;
  business_id: number | null;
  /** 用户信息（兼容：来自 userinfo ?? account_info） */
  userInfo: UserInfo | null;
  /** 登录接口完整缓存（与 /login 返回结构一致），用于刷新后恢复 SDK update */
  loginData: PersistedLoginData | null;
  isAuthenticated: boolean;

  setAccessToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  setTokens: (
    accessToken: string,
    refreshToken: string,
    expiresTime?: number,
  ) => void;
  setExpiresTime: (expiresTime: number | null) => void;
  setUserId: (userId: number) => void;
  setProductId: (productId: number) => void;
  setBusinessId: (businessId: number) => void;
  setUserInfo: (userInfo: UserInfo) => void;
  /** 设置登录接口完整数据（userinfo/account_info/connection_info/basic），并同步 userInfo */
  setLoginData: (data: {
    userinfo?: OAuthLoginUserinfo | null;
    account_info?: OAuthLoginAccountInfo | null;
    connection_info?: OAuthLoginConnectionInfo | null;
    basic?: OAuthLoginBasic | null;
  }) => void;
  clearAuth: () => void;
  updateAuthStatus: () => void;
}

type PersistedAuthState = Pick<
  AuthState,
  | "access_token"
  | "refresh_token"
  | "expires_time"
  | "user_id"
  | "product_id"
  | "business_id"
  | "userInfo"
  | "loginData"
  | "isAuthenticated"
>;

/**
 * 认证状态管理 Store
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      access_token: null,
      refresh_token: null,
      expires_time: null,
      user_id: null,
      product_id: null,
      business_id: null,
      userInfo: null,
      loginData: null,
      isAuthenticated: false,

      setAccessToken: (token) => {
        set({ access_token: token });
        get().updateAuthStatus();
      },

      setRefreshToken: (token) => {
        set({ refresh_token: token });
        get().updateAuthStatus();
      },

      setExpiresTime: (expiresTime) => {
        set({ expires_time: expiresTime });
      },

      setUserId: (userId) => {
        set({ user_id: userId });
        LogUtil.debug("[AuthStore] User ID 已设置:", userId);
      },

      setProductId: (productId) => {
        set({ product_id: productId });
        LogUtil.debug("[AuthStore] Product ID 已设置:", productId);
      },

      setBusinessId: (businessId) => {
        set({ business_id: businessId });
        LogUtil.debug("[AuthStore] Business ID 已设置:", businessId);
      },

      setTokens: (accessToken, refreshToken, expiresTime) => {
        set({
          access_token: accessToken,
          refresh_token: refreshToken,
          ...(expiresTime !== undefined && {
            expires_time: expiresTime || null,
          }),
          isAuthenticated: true,
        });
      },

      setUserInfo: (userInfo) => {
        set({ userInfo });
      },

      setLoginData: (data) => {
        const userinfo = data.userinfo ?? null;
        const account_info = data.account_info ?? null;
        const connection_info = data.connection_info ?? null;
        const basic = data.basic ?? null;
        const userInfo = (userinfo ?? account_info) as UserInfo | null;
        set({
          loginData: {
            userinfo,
            account_info,
            connection_info,
            basic,
          },
          userInfo,
        });
        if (userinfo || account_info) {
          LogUtil.debug("[AuthStore] 登录数据已缓存（loginData）");
        }
      },

      clearAuth: () => {
        set({
          access_token: null,
          refresh_token: null,
          expires_time: null,
          user_id: null,
          product_id: null,
          business_id: null,
          userInfo: null,
          loginData: null,
          isAuthenticated: false,
        });
      },

      updateAuthStatus: () => {
        const { access_token, refresh_token } = get();
        set({
          isAuthenticated: !!(access_token && refresh_token),
        });
      },
    }),
    {
      name: PERSIST_STORAGE_NAMES.auth,
      storage: createAppConfigPersistStorage<PersistedAuthState>("auth"),
      partialize: (state): PersistedAuthState => ({
        access_token: state.access_token,
        refresh_token: state.refresh_token,
        expires_time: state.expires_time,
        user_id: state.user_id,
        product_id: state.product_id,
        business_id: state.business_id,
        userInfo: state.userInfo,
        loginData: state.loginData,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
